import React, { createContext, useContext, useState, useCallback } from 'react';

interface VoteUpdate {
  promptId: string;
  newVoteCount: number;
  userVote: number | null;
}

interface VoteContextType {
  notifyVoteChange: (promptId: string, newVoteCount: number, userVote: number | null) => void;
  subscribeToVoteChanges: (promptId: string, callback: (voteUpdate: VoteUpdate) => void) => () => void;
}

const VoteContext = createContext<VoteContextType | undefined>(undefined);

export const VoteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscribers, setSubscribers] = useState<Map<string, Set<(voteUpdate: VoteUpdate) => void>>>(new Map());

  const notifyVoteChange = useCallback((promptId: string, newVoteCount: number, userVote: number | null) => {
    const promptSubscribers = subscribers.get(promptId);
    if (promptSubscribers) {
      const voteUpdate: VoteUpdate = { promptId, newVoteCount, userVote };
      promptSubscribers.forEach(callback => {
        try {
          callback(voteUpdate);
        } catch (error) {
          // Silently handle callback errors
        }
      });
    }
  }, [subscribers]);

  const subscribeToVoteChanges = useCallback((promptId: string, callback: (voteUpdate: VoteUpdate) => void) => {
    setSubscribers(prev => {
      const newSubscribers = new Map(prev);
      if (!newSubscribers.has(promptId)) {
        newSubscribers.set(promptId, new Set());
      }
      newSubscribers.get(promptId)!.add(callback);
      return newSubscribers;
    });

    // Return unsubscribe function
    return () => {
      setSubscribers(prev => {
        const newSubscribers = new Map(prev);
        const promptSubscribers = newSubscribers.get(promptId);
        if (promptSubscribers) {
          promptSubscribers.delete(callback);
          if (promptSubscribers.size === 0) {
            newSubscribers.delete(promptId);
          }
        }
        return newSubscribers;
      });
    };
  }, []);

  return (
    <VoteContext.Provider value={{ notifyVoteChange, subscribeToVoteChanges }}>
      {children}
    </VoteContext.Provider>
  );
};

export const useVoteSync = () => {
  const context = useContext(VoteContext);
  if (context === undefined) {
    throw new Error('useVoteSync must be used within a VoteProvider');
  }
  return context;
}; 